import React, { useState, useMemo, useCallback } from 'react';
import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { DndContext, closestCenter } from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { create, all } from 'mathjs';
import './Resource.css';

// Initialize mathjs
const math = create(all);
// Define a custom base unit 'u' for "units" or other dimensionless quantities
math.createUnit('u');

// Sortable parameter item component
const SortableParamItem = ({ id, children }) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
  } = useSortable({ id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  };

  const child = React.Children.only(children);
  const handle = React.cloneElement(child.props.children[0], { ...listeners });

  return (
    <div ref={setNodeRef} style={style} {...attributes}>
      {React.cloneElement(child, {}, [handle, ...React.Children.toArray(child.props.children).slice(1)].map((c, i) => React.cloneElement(c, { key: i })))}
    </div>
  );
};

// Helper function to convert a string to a deterministic hex color (currently unused)
// const stringToHexColor = (str) => {
//   if (typeof str !== 'string') {
//     str = String(str);
//   }
//   let hash = 0;
//   for (let i = 0; i < str.length; i++) {
//     hash = str.charCodeAt(i) + ((hash << 5) - hash);
//   }
//
//   let color = '#';
//   for (let i = 0; i < 3; i++) {
//     const value = (hash >> (i * 8)) & 0xFF;
//     color += ('00' + value.toString(16)).substring(value.toString(16).length - 2);
//   }
//
//   return color;
// };

// --- Formula Logic using mathjs (from ShapeListItem) ---
// Normalize unicode primes and superscripts
const normalizeSymbols = (str) => {
  if (typeof str !== 'string') return str;
  return str
    .replace(/\u2032/g, "'") // ′
    .replace(/\u2033/g, '"') // ″
    .replace(/²/g, '^2')
    .replace(/³/g, '^3');
};

// Convert imperial notations to mathjs units inside an arbitrary string
const toMathjsImperial = (input) => {
  if (input == null) return input;
  let str = String(input).trim();
  if (!str) return str;

  str = normalizeSymbols(str);

  // feet + inches, allow space or hyphen separator: 16' 4" | 16'-4" | -16' 4.5"
  const feetInches = /([+-]?\d+(?:\.\d+)?)\s*'\s*[-\s]*([+-]?\d+(?:\.\d+)?)\s*"?/g;
  str = str.replace(feetInches, (_, f, i) => {
    const fNum = parseFloat(f);
    const iNum = parseFloat(i);
    const op = (String(i).trim().startsWith('-') || fNum < 0) ? '-' : '+';
    const iAbs = Math.abs(iNum);
    // wrap in parentheses to preserve precedence in larger expressions
    return `(${f} ft ${op} ${iAbs} in)`;
  });

  // standalone feet: 10'
  const feetOnly = /([+-]?\d+(?:\.\d+)?)\s*'/g;
  str = str.replace(feetOnly, (_, f) => `${f} ft`);

  // standalone inches: 8"
  const inchesOnly = /([+-]?\d+(?:\.\d+)?)\s*"/g;
  str = str.replace(inchesOnly, (_, i) => `${i} in`);

  // common area/unit variants
  str = str.replace(/\bft²\b/g, 'ft^2').replace(/\bin²\b/g, 'in^2');

  return str;
};

const evaluateFormula = (formula, context) => {
  const scope = {};
  for (const key in context) {
    const value = context[key];
    const sanitizedKey = key.replace(/[^A-Z0-9]/gi, '');
    if (!sanitizedKey) continue;

    try {
      if (value && value.isUnit) {
        scope[sanitizedKey] = value;
      } else {
        const converted = toMathjsImperial(String(value));
        try {
          const parsed = math.evaluate(converted);
          scope[sanitizedKey] = parsed;
        } catch {
          // If parse fails, store as string or number
          const num = parseFloat(value);
          scope[sanitizedKey] = isNaN(num) ? value : num;
        }
      }
    } catch {
      scope[sanitizedKey] = value;
    }
  }

  const expr = toMathjsImperial(formula.startsWith('=') ? formula.slice(1) : formula);
  return math.evaluate(expr, scope);
};

const resolveAllParams = (resourceParams, calculatedParams) => {
  let resolved = { ...calculatedParams };
  let formulasToSolve = {};

  // Separate static values from formulas
  for (const key in resourceParams) {
    if (key === 'order' || typeof resourceParams[key] === 'object') continue;
    const value = resourceParams[key];
    if (typeof value === 'string' && value.startsWith('=')) {
      formulasToSolve[key] = value;
    } else {
      resolved[key] = value;
    }
  }

  const maxIterations = 10;
  let i = 0;
  let lastUnresolved = -1;

  while (Object.keys(formulasToSolve).length > 0 && i < maxIterations) {
    const currentUnresolved = Object.keys(formulasToSolve).length;
    if (lastUnresolved === currentUnresolved) break; // no progress
    lastUnresolved = currentUnresolved;

    const stillUnresolved = {};

    for (const key in formulasToSolve) {
      const formula = formulasToSolve[key];
      try {
        const result = evaluateFormula(formula, resolved);
        resolved[key] = result;
      } catch {
        // Defer marking as error until after attempts finish.
        stillUnresolved[key] = formula;
      }
    }
    formulasToSolve = stillUnresolved;
    i++;
  }

  // Remaining unresolved formulas -> dependency (#REF!) or circular/error
  for (const key in formulasToSolve) {
    resolved[key] = { error: '#REF!' };
  }

  return resolved;
};

// Helper to format the final display value
const formatDisplayValue = (value) => {
  if (value?.error) return value.error;
  if (value instanceof math.Unit) {
    return math.format(value, { notation: 'fixed', precision: 1 });
  }
  if (typeof value === 'number') return value.toFixed(1);
  return value;
};

const Resource = ({
  id,
  name,
  role,
  skillLevel,
  isTemp = false,
  isManager = false,
  onDelete,
  isInPool = false,
  params = {}, // Custom parameters
  tags = [], // Tags array
  onUpdateParams, // Callback to update params
  onUpdateTags, // Callback to update tags
  count = 1, // For stacking identical resources
  resourceIds = [], // For stacking
  activeDetailView = 'info', // Controlled by parent (ResourcePool)
  isExpanded = false, // Controlled by parent
  onToggleExpand, // Callback for expand/collapse
}) => {
  const [showActions, setShowActions] = useState(false);
  const [paramInputs, setParamInputs] = useState({ key: '', value: '' });
  const [editingParam, setEditingParam] = useState(null);
  const [isAddingParam, setIsAddingParam] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);

  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id, data: { type: 'resource' } });

  // Role color configuration
  const ROLE_COLORS = useMemo(() => ({
    'Manager': { bg: '#8b5cf6', border: '#7c3aed', avatar: 'linear-gradient(135deg, #a78bfa 0%, #8b5cf6 100%)' },
    'Developer': { bg: '#3b82f6', border: '#2563eb', avatar: 'linear-gradient(135deg, #60a5fa 0%, #3b82f6 100%)' },
    'Designer': { bg: '#ec4899', border: '#db2777', avatar: 'linear-gradient(135deg, #f472b6 0%, #ec4899 100%)' },
    'Tester': { bg: '#10b981', border: '#059669', avatar: 'linear-gradient(135deg, #34d399 0%, #10b981 100%)' },
    'Analyst': { bg: '#f59e0b', border: '#d97706', avatar: 'linear-gradient(135deg, #fbbf24 0%, #f59e0b 100%)' },
    'Architect': { bg: '#6366f1', border: '#4f46e5', avatar: 'linear-gradient(135deg, #818cf8 0%, #6366f1 100%)' },
  }), []);

  // Skill level opacity configuration
  const SKILL_LEVEL_OPACITY = useMemo(() => ({
    'Junior': 0.7,
    'Mid': 0.85,
    'Senior': 1.0,
  }), []);

  // Memoized color and opacity values
  const roleColor = useMemo(
    () => ROLE_COLORS[role] || { bg: '#64748b', border: '#475569', avatar: 'linear-gradient(135deg, #94a3b8 0%, #64748b 100%)' },
    [role, ROLE_COLORS]
  );

  const skillOpacity = useMemo(
    () => SKILL_LEVEL_OPACITY[skillLevel] || 0.8,
    [skillLevel, SKILL_LEVEL_OPACITY]
  );

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    '--role-bg': roleColor.bg,
    '--role-border': roleColor.border,
    '--role-avatar': roleColor.avatar,
    '--skill-opacity': skillOpacity,
  };

  // Helper: target ids to apply param changes to
  const targetResourceIds = useMemo(
    () => (Array.isArray(resourceIds) && resourceIds.length > 0) ? resourceIds : [id],
    [resourceIds, id]
  );

  // Helper to apply params update to all resources in the stack
  const applyParamsToStack = useCallback((paramsUpdater) => {
    if (!onUpdateParams) return;
    targetResourceIds.forEach(resId => {
      const updatedParams = paramsUpdater(params, resId);
      onUpdateParams(resId, updatedParams);
    });
  }, [onUpdateParams, targetResourceIds, params]);

  // Tag handlers
  const handleTagAdd = useCallback((newTag) => {
    if (!onUpdateTags || !newTag.trim()) return;
    const updatedTags = [...tags, newTag.trim()];
    targetResourceIds.forEach(resId => {
      onUpdateTags(resId, updatedTags);
    });
  }, [onUpdateTags, tags, targetResourceIds]);

  const handleTagRemove = useCallback((tagToRemove) => {
    if (!onUpdateTags) return;
    const updatedTags = tags.filter(tag => tag !== tagToRemove);
    targetResourceIds.forEach(resId => {
      onUpdateTags(resId, updatedTags);
    });
  }, [onUpdateTags, tags, targetResourceIds]);

  // Parameter handlers
  const handleParamAdd = useCallback(() => {
    const { key, value } = paramInputs;
    if (!onUpdateParams || !key?.trim() || !value?.trim()) return;

    applyParamsToStack((baseParams) => {
      const trimmedKey = key.trim();
      const updatedParams = { ...(baseParams || {}), [trimmedKey]: value.trim() };
      const updatedOrder = baseParams?.order ? [...baseParams.order, trimmedKey] : [trimmedKey];
      return { ...updatedParams, order: updatedOrder };
    });

    setParamInputs({ key: '', value: '' });
    setIsAddingParam(false);
  }, [paramInputs, onUpdateParams, applyParamsToStack]);

  const handleParamRemove = useCallback((keyToRemove) => {
    if (!onUpdateParams) return;

    applyParamsToStack((baseParams) => {
      const { [keyToRemove]: _, order, ...rest } = (baseParams || {});
      const updatedOrder = order ? order.filter(k => k !== keyToRemove) : [];
      return { ...rest, order: updatedOrder };
    });
  }, [onUpdateParams, applyParamsToStack]);

  const handleParamUpdate = useCallback((key, newValue) => {
    if (!onUpdateParams) return;

    applyParamsToStack((baseParams) => {
      const updatedParams = { ...(baseParams || {}), [key]: newValue.trim() };
      return updatedParams;
    });

    setEditingParam(null);
  }, [onUpdateParams, applyParamsToStack]);

  // Drag end handler for parameter reordering
  const handleDragEnd = useCallback((event) => {
    const { active, over } = event;

    if (!over || active.id === over.id) {
      return;
    }

    const orderedKeys = params.order || [];
    const oldIndex = orderedKeys.indexOf(active.id);
    const newIndex = orderedKeys.indexOf(over.id);

    if (oldIndex === -1 || newIndex === -1) return;

    const updatedOrder = arrayMove(orderedKeys, oldIndex, newIndex);

    if (onUpdateParams) {
      onUpdateParams(id, { ...params, order: updatedOrder });
    }
  }, [params, onUpdateParams, id]);

  // Get ordered parameter keys
  const orderedKeys = useMemo(
    () => params?.order || Object.keys(params || {}).filter(k => k !== 'order'),
    [params]
  );

  // Check if there are any details to show
  const hasDetails = useMemo(
    () => (tags && tags.length > 0) || Object.keys(params || {}).length > 0 || true,
    [tags, params]
  );

  return (
    <div className="resource-list-item">
      <div
        className="resource-wrapper"
        onMouseEnter={() => isInPool && setShowActions(true)}
        onMouseLeave={() => isInPool && setShowActions(false)}
      >
        <div className="resource-list-header">
          {/* Expand button */}
          {hasDetails && onToggleExpand && (
            <button
              className="expand-button"
              onClick={onToggleExpand}
              title={isExpanded ? "Hide details" : "Show details"}
            >
              {isExpanded ? '▼' : '▶'}
            </button>
          )}
          <div
            ref={setNodeRef}
            style={style}
            className={`resource ${isDragging ? 'dragging' : ''} ${isTemp ? 'resource-temp' : ''}`}
            {...attributes}
            {...listeners}
          >
            {/* Compact Avatar */}
            <div className="resource-avatar">
              {name.charAt(0).toUpperCase()}
            </div>

            {/* Resource Name Container */}
            <div className="resource-name-container">
              <span className="resource-name">
                {count > 1 && <i>{count} x </i>}
                {name}
              </span>
              {skillLevel && <span className="resource-badge">{skillLevel.charAt(0)}</span>}
            </div>
          </div>

        </div>

        {/* Detail Content */}
        {isExpanded && (
          <div className="detail-content">
            {/* Info View */}
            {activeDetailView === 'info' && (
              <div className="resource-details">
                <div className="detail-item">Role: {role}</div>
                <div className="detail-item">Skill Level: {skillLevel}</div>
                <div className="detail-item">Type: {isManager ? 'Manager' : 'Team Member'}</div>
                {count > 1 && <div className="detail-item">Count: {count}</div>}
              </div>
            )}

            {/* Tags View */}
            {activeDetailView === 'tags' && (
              <>
                <div className="resource-tags-container">
                  {tags && tags.map((tag, index) => (
                    <span key={index} className="resource-tag">
                      {tag}
                      <button className="remove-tag-button" onClick={() => handleTagRemove(tag)}>&times;</button>
                    </span>
                  ))}
                </div>
                <input
                  type="text"
                  className="resource-tags-input"
                  placeholder="Add a tag and press Enter..."
                  onClick={(e) => e.stopPropagation()}
                  onKeyDown={(e) => {
                    e.stopPropagation();
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      handleTagAdd(e.target.value);
                      e.target.value = '';
                    }
                  }}
                />
              </>
            )}

            {/* Parameters View */}
            {activeDetailView === 'params' && (
              <div className="resource-params-container">
                {(() => {
                  // Calculate resolved parameters with formulas
                  const calculatedParams = {
                    Role: role,
                    'Skill Level': skillLevel,
                    Type: isManager ? 'Manager' : 'Team Member',
                  };
                  if (count > 1) {
                    calculatedParams['Count'] = count;
                  }

                  const allResolvedParams = resolveAllParams(params, calculatedParams);

                  // Get ordered parameter keys, excluding 'order' itself
                  const orderedKeys = params?.order || Object.keys(params || {}).filter(k => k !== 'order');

                  return (
                    <>
                      {/* User-Defined Params (sortable) */}
                      <DndContext
                        collisionDetection={closestCenter}
                        onDragEnd={handleDragEnd}
                      >
                        <SortableContext
                          items={orderedKeys}
                          strategy={verticalListSortingStrategy}
                        >
                          {orderedKeys.map((key) => {
                            if (key === 'order') return null;
                            const value = params[key];
                            if (value === undefined) return null;

                            const isEditing = editingParam?.key === key;
                            const displayValue = formatDisplayValue(allResolvedParams[key]);
                            const isFormula = typeof value === 'string' && value.startsWith('=');

                            return (
                              <SortableParamItem key={key} id={key} style={{ padding: '0px' }}>
                                <div className="resource-param-item">
                                  <span className="resource-param-key" style={{ cursor: 'grab' }}>{key}:</span>
                                  {isEditMode || isEditing ? (
                                    <inputs
                                      type="text"
                                      className="resource-param-value-input"
                                      value={editingParam?.value ?? value}
                                      onChange={(e) => setEditingParam({ key, value: e.target.value })}
                                      onBlur={() => {
                                        if (editingParam?.value !== undefined) {
                                          handleParamUpdate(key, editingParam.value);
                                        }
                                      }}
                                      onKeyDown={(e) => {
                                        if (e.key === 'Enter') {
                                          e.preventDefault();
                                          handleParamUpdate(key, editingParam?.value ?? value);
                                        }
                                        if (e.key === 'Escape') {
                                          setEditingParam(null);
                                        }
                                      }}
                                      autoFocus
                                    />
                                  ) : (
                                    <span
                                      className="resource-param-value"
                                      onClick={() => setEditingParam({ key, value })}
                                      title={isFormula ? `Formula: ${value}` : ''}
                                    >
                                      {displayValue || '(empty)'}
                                    </span>
                                  )}
                                  {isEditMode && (
                                    <button className="remove-tag-button" onClick={() => handleParamRemove(key)}>&times;</button>
                                  )}
                                </div>
                              </SortableParamItem>
                            );
                          })}
                        </SortableContext>
                      </DndContext>

                      {isAddingParam && (
                        <div className="resource-param-input-group">
                          <input
                            type="text"
                            className="resource-param-input"
                            placeholder="Key"
                            value={paramInputs.key}
                            onChange={(e) => setParamInputs({ ...paramInputs, key: e.target.value })}
                          />
                          <input
                            type="text"
                            className="resource-param-input"
                            placeholder="Value (or =formula)"
                            value={paramInputs.value}
                            onChange={(e) => setParamInputs({ ...paramInputs, value: e.target.value })}
                            onKeyDown={(e) => {
                              if (e.key === 'Enter') {
                                e.preventDefault();
                                handleParamAdd();
                              }
                            }}
                          />
                        </div>
                      )}

                    </>
                  );
                })()}

                {/* Footer */}
                {isExpanded && (
                  <div className="resource-list-footer">
                    {/* Edit Mode Toggle Button */}
                    <button 
                      className="add-param-toggle-button" 
                      onClick={() => setIsEditMode(!isEditMode)} 
                      title={isEditMode ? 'Exit Edit Mode' : 'Enter Edit Mode'}
                    >
                      ✏️
                    </button>

                    {/* Add Custom Parameter Button */}
                    <button className="add-param-toggle-button" onClick={() => setIsAddingParam(!isAddingParam)} title={isAddingParam ? 'Cancel' : 'Add Custom Parameter'}>
                      {isAddingParam ? '✕' : '➕'}
                    </button>

                    {/* Save Parameter Button (only show when adding) */}
                    {isAddingParam && (
                      <button className="add-param-button" onClick={handleParamAdd} title="Save Parameter">✓</button>
                    )}

                    {/* Delete Resource Button */}
                    {isInPool && onDelete && (
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          onDelete(id);
                        }}
                        className="resource-action-btn delete"
                        title="Delete resource"
                      >
                        🗑️
                      </button>
                    )}
                  </div>
                )}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default Resource;
